import React, { useState, useRef } from "react";
import axiosInstance from "../../axiosConfig";
import { Button } from "@mui/material";
import {
  FilePond,
  registerPlugin,
  type FilePond as ReactFilePond,
} from "react-filepond";
import type { FilePond as FilePondInstance } from "filepond";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import "filepond/dist/filepond.min.css";
import { RenderIf, LoadingSpinner, Modal } from "@adl/smart-ui-library";
import { Box } from "@mui/material";

registerPlugin(FilePondPluginFileValidateSize);

const FileUploadModal = ({ onCancel, metadata }) => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [filename, setFilename] = useState("");
  const [filenameError, setFilenameError] = useState("");
  const filePondRef = useRef<ReactFilePond | null>(null);

  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);

  const validateFilename = (name) => {
    const regex = /^[a-zA-Z0-9 ._()-]+$/;
    if (!regex.test(name)) {
      setFilenameError("File name cannot have special characters.");
      return false;
    }
    setFilenameError("");
    return true;
  };

  const handleUpload = async () => {
    if (!validateFilename(filename)) {
      return;
    }

    setIsUploading(true);

    if (!filePondRef.current) {
      setMessage("Please select a file first!");
      return;
    }

    // Cast the ref to FilePondInstance to access getFiles()
    const pondInstance = filePondRef.current as unknown as FilePondInstance;
    const files = pondInstance.getFiles();
    if (files.length === 0) {
      setMessage("Please select a file first!");
      return;
    }

    const formData = new FormData();
    files.forEach((file) => {
      formData.append("file", file.file);
    });

    formData.append("metadata", JSON.stringify(metadata));

    try {
      const response = await axiosInstance.post(
        "documents/upload/level2",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessage(response.data.message || "File uploaded successfully!");
      setUploadSuccess(true);
      setIsUploading(false);
    } catch (error: any) {
      setMessage("Error uploading file: " + error.message);
      setError(true);
      setIsUploading(false);
    }
  };

  return (
    <Modal open={true} onClose={onCancel} name="delete-history-confirmation">
      <Modal.Header>Upload Sanitised File</Modal.Header>
      <Modal.Content>
        <div>
          <RenderIf isTrue={isUploading}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 299,
                borderRadius: "10px",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              }}
            >
              <LoadingSpinner />
            </Box>
          </RenderIf>
          {!error && !uploadSuccess ? (
            <>
              <FilePond
                allowFileSizeValidation={true}
                maxFileSize={`100MB`}
                ref={filePondRef}
                allowMultiple={false}
                maxFiles={1}
                instantUpload={false}
                name="files"
                labelIdle='Drag & Drop your file or <span class="filepond--label-action">Browse</span>'
                acceptedFileTypes={[
                  "application/pdf",
                  "image/jpeg",
                  "image/png",
                  'text/markdown',
                  'text/x-markdown'
                ]}
                onaddfile={(error, file) => {
                  if (!error) {
                    setFilename(file.filename);
                    validateFilename(file.filename);
                  }
                }}
              />
              <Box component="span" sx={{ color: "red" }}>
                {filenameError}
              </Box>
            </>
          ) : (
            <p>{message}</p>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          variant="outlined"
          disableElevation
          onClick={onCancel}
          sx={{ width: "100%" }}
        >
          Cancel
        </Button>
        <RenderIf isTrue={error || uploadSuccess}>
          <Button
            variant="contained"
            disableElevation
            disabled={isUploading}
            onClick={onCancel}
            sx={{ width: "100%" }}
          >
            Okay
          </Button>
        </RenderIf>
        <RenderIf isTrue={!error && !uploadSuccess}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleUpload}
            sx={{ width: "100%" }}
            disabled={!!filenameError}
          >
            Upload
          </Button>
        </RenderIf>
      </Modal.Footer>
    </Modal>
  );
};

export default FileUploadModal;
